/**
 * Mapbox タイルから避難先の実形状 (建物フットプリント) を取得する。
 *
 * Overpass (公開サーバ) は 429/スロットルで不安定だが、Mapbox のベクタタイルは
 * 既にアプリが表示に使っていて即時・無制限に読める。そこで**画面外の使い捨て地図**
 * (プローブ) を対象点の上に z16 で立て、`composite/building` ソースを
 * `querySourceFeatures` し、点を内包する建物を実形状として返す。
 *
 * プローブは高速化のため 1 枚を使い回す (jumpTo で移動)。初回のみ生成コスト
 * (~2-3s)、以降は ~0.6s/地点 (実測)。地図の生成・破棄は viewport-png.ts の
 * オフスクリーン方式に倣う。純粋な形状抽出は lib/map/building-footprint.ts。
 *
 * 注意 (教訓15): タブが hidden だと rAF が止まり `idle` が永遠に来ない。本番の
 * アプリタブは可視なので問題ないが、検証 (ブラウザペイン) では rAF シムが要る。
 * ここでは idle にタイムアウトを併設し、来なくてもロード済みタイルで問い合わせる。
 */
import { ringAreaM2 } from '@/lib/evacuation-sites/open-space';
import mapboxgl from 'mapbox-gl';
import type { LngLat } from '@/types/property';
import { MAPBOX_TOKEN } from '@/services/mapbox';
import type { BridgeCrossing } from '@/services/overpass';
import {
  buildingRingFromFeatures,
  groundsRingFromFeatures,
  type TileBuildingFeature,
} from '@/lib/map/building-footprint';
import { bridgesFromRoadFeatures } from '@/lib/map/bridge-detect';
import {
  boundaryEntriesFromRoadFeatures,
  nearbyRoadPointsFromFeatures,
} from '@/lib/map/boundary-entries';
import { roadCenterlinesFromFeatures } from '@/lib/map/road-centerlines';
import {
  bboxOfPoints,
  fetchRoadFeaturesForBbox,
} from '@/services/mapbox-road-tiles';

/** 取得した形状の種別。bake 側の置換ルール (敷地なら既存を置換等) が参照する。 */
export interface MapboxSiteShape {
  ring: Array<[number, number]>;
  kind: 'grounds' | 'building';
}

// building が確実に載る素の streets スタイル (composite = mapbox-streets-v8)。
// アプリの applyEvacuationStyle はレイヤーを隠すため、プローブには使わない。
const PROBE_STYLE = 'mapbox://styles/mapbox/streets-v12';
/**
 * 建物・敷地の初期ズーム。小さいフットプリント/校地は精度が要るので高く取る。
 */
const BUILDING_ZOOM = 16;
/**
 * 敷地が切れているときにズームを下げる下限。
 *
 * z16 は 512px 視野が ~500m しか覆わず、大公園 (天王寺公園 = 南北730m) が
 * タイル境界で切れる (landuse は feature id を持たず視野外の断片を復元できない)。
 * 敷地が視野いっぱい = 切れている恐れ、のときだけズームを 2 段ずつ下げて読み直し、
 * 全体が視野に収まる (面が増えなくなる) まで繰り返す。z12 で視野 ~10km。
 */
const MIN_GROUNDS_ZOOM = 12;
/**
 * 敷地サイズの上限 (対角, m)。ズームを下げて読み広げる際、これを超えたら止める。
 *
 * ズームを下げすぎると川沿いの帯状緑地 (江南緑地公園など数km) や海浜公園を丸ごと
 * 連結してしまい、避難範囲として無意味な巨大ポリゴンになる。天王寺公園 (対角~1km) の
 * ようなコンパクトな公園は上限内で全体が取れ、帯状はこの手前で止まる。
 */
const GROUNDS_MAX_DIAG_M = 1500;
/** プローブ生成時の初期ズーム。 */
const PROBE_ZOOM = BUILDING_ZOOM;
/** 敷地が視野の縦横いずれかでこの割合を超える = 端で切れている恐れ → ズームを下げる。 */
const REFRAME_FILL_RATIO = 0.8;
const READY_TIMEOUT_MS = 12_000;
const IDLE_TIMEOUT_MS = 8_000;
// 一定時間フェッチが無ければプローブを破棄して WebGL コンテキストを解放する。
// 連続フェッチ (一括再ベイク等) の間はタイマーが更新され続けて生存する。
const INACTIVITY_MS = 90_000;

let probe: mapboxgl.Map | null = null;
let probeReady: Promise<mapboxgl.Map> | null = null;
// プローブは 1 枚しか無い = 同時に 1 地点しか見られない。呼び出しを直列化する。
let chain: Promise<unknown> = Promise.resolve();
let idleTimer: ReturnType<typeof setTimeout> | null = null;

function createProbe(): Promise<mapboxgl.Map> {
  const holder = document.createElement('div');
  holder.style.cssText =
    'position:fixed;left:-99999px;top:0;width:512px;height:512px;pointer-events:none;';
  document.body.appendChild(holder);
  if (MAPBOX_TOKEN) mapboxgl.accessToken = MAPBOX_TOKEN;
  const map = new mapboxgl.Map({
    container: holder,
    style: PROBE_STYLE,
    center: [139.767, 35.681],
    zoom: PROBE_ZOOM,
    interactive: false,
    attributionControl: false,
    preserveDrawingBuffer: false,
    fadeDuration: 0,
  });
  probe = map;
  return new Promise((resolve, reject) => {
    let done = false;
    const succeed = () => {
      if (!done) {
        done = true;
        resolve(map);
      }
    };
    const fail = (err: unknown) => {
      if (!done) {
        done = true;
        reject(err instanceof Error ? err : new Error('probe map error'));
      }
    };
    map.once('load', succeed);
    // スタイル/タイルのロードが致命的に失敗したら reject → 呼び出し側が作り直す。
    map.once('error', (e: { error?: Error }) => fail(e?.error));
    // load も error も来なくても (環境要因) 進めるよう保険。
    window.setTimeout(succeed, READY_TIMEOUT_MS);
  });
}

/** 非アクティブが続いたらプローブを破棄するタイマーを張り直す。 */
function scheduleIdleDispose(): void {
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(disposeMapboxProbe, INACTIVITY_MS);
}

/** idle 待ち (来なければタイムアウトでロード済みタイルに対して問い合わせる)。 */
function waitIdle(map: mapboxgl.Map): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      map.off('idle', finish);
      resolve();
    };
    map.once('idle', finish);
    window.setTimeout(finish, IDLE_TIMEOUT_MS);
  });
}

/** areTilesLoaded ポーリングの上限。 */
const TILES_TIMEOUT_MS = 5_000;

/**
 * jumpTo 後にタイルが確実にロードされるまで待つ。
 *
 * `idle` は jumpTo 直後の「タイル要求が発行される前」に発火し得る (レース) ため、
 * idle だけを待つと未ロードのタイルで querySourceFeatures してしまい、結果が
 * ランダムに少なくなる (実測: 阪本小学校の進入点が cold 時に欠ける)。
 * 1フレームの settle → idle → areTilesLoaded の三段で確実化する。
 */
async function waitTilesReady(map: mapboxgl.Map): Promise<void> {
  // jumpTo 直後の描画フレームでタイル要求を発行させる
  await new Promise((r) => window.setTimeout(r, 120));
  await waitIdle(map);
  const t0 = Date.now();
  while (Date.now() - t0 < TILES_TIMEOUT_MS) {
    try {
      if (map.areTilesLoaded()) return;
    } catch {
      return; // map 破棄等 — 呼び出し側の catch に任せる
    }
    await new Promise((r) => window.setTimeout(r, 100));
  }
}

/** リングの bbox [w, s, e, n]。 */
function ringBounds(
  ring: Array<[number, number]>,
): [number, number, number, number] {
  let w = Infinity;
  let s = Infinity;
  let e = -Infinity;
  let n = -Infinity;
  for (const [x, y] of ring) {
    if (x < w) w = x;
    if (y < s) s = y;
    if (x > e) e = x;
    if (y > n) n = y;
  }
  return [w, s, e, n];
}

/** リング bbox の対角線長 (m, 概算)。 */
function ringDiagM(ring: Array<[number, number]>): number {
  const [w, s, e, n] = ringBounds(ring);
  const kx = Math.cos(((s + n) / 2) * (Math.PI / 180)) * 111_320;
  return Math.hypot((e - w) * kx, (n - s) * 111_320);
}

async function doFetch(center: LngLat): Promise<MapboxSiteShape | null> {
  if (!probeReady) probeReady = createProbe();
  let map: mapboxgl.Map;
  try {
    map = await probeReady;
  } catch {
    // プローブ生成に失敗 → 破棄して次回作り直す (壊れたプローブを掴み続けない)。
    disposeMapboxProbe();
    return null;
  }
  const pt: [number, number] = [center.lng, center.lat];
  const queryGrounds = () =>
    groundsRingFromFeatures(
      map.querySourceFeatures('composite', {
        sourceLayer: 'landuse',
      }) as unknown as TileBuildingFeature[],
      pt,
    );
  /** 敷地が現在の視野の縦横いずれかを REFRAME_FILL_RATIO 超で占める = 切れの恐れ。 */
  const clipRisk = (ring: Array<[number, number]>): boolean => {
    const bnd = map.getBounds();
    if (!bnd) return false;
    const b = ringBounds(ring);
    return (
      (b[2] - b[0]) / (bnd.getEast() - bnd.getWest()) > REFRAME_FILL_RATIO ||
      (b[3] - b[1]) / (bnd.getNorth() - bnd.getSouth()) > REFRAME_FILL_RATIO
    );
  };
  try {
    // --- 敷地 (landuse): まず z16 で詳細に読む ---
    map.jumpTo({ center: pt, zoom: BUILDING_ZOOM });
    await waitTilesReady(map);
    let grounds = queryGrounds();
    if (grounds) {
      // 視野いっぱい = 切れている恐れ。全体が収まる (面が増えなくなる) まで
      // ズームを 2 段ずつ下げて読み直す (視野を広げてタイルを追加ロード)。
      // ただし帯状緑地の暴走を防ぐため、上限サイズを超えたら直前で止める。
      let z = BUILDING_ZOOM;
      while (
        clipRisk(grounds) &&
        z > MIN_GROUNDS_ZOOM &&
        ringDiagM(grounds) < GROUNDS_MAX_DIAG_M
      ) {
        z -= 2;
        map.jumpTo({ center: pt, zoom: z });
        await waitTilesReady(map);
        const grown = queryGrounds();
        if (!grown) break;
        // 広げた結果が上限超 = 帯状/巨大 → 採用せず直前 (小さい方) を保持。
        if (ringDiagM(grown) > GROUNDS_MAX_DIAG_M) break;
        const a0 = ringBounds(grounds);
        const a1 = ringBounds(grown);
        const grew =
          (a1[2] - a1[0]) * (a1[3] - a1[1]) >
          (a0[2] - a0[0]) * (a0[3] - a0[1]) * 1.01;
        grounds = grown;
        if (!grew) break; // これ以上広がらない = 全体を捉えた
      }
      scheduleIdleDispose();
      return { ring: grounds, kind: 'grounds' };
    }

    // --- 建物: 敷地が無い施設だけ (視野は既に z16) ---
    const bld = buildingRingFromFeatures(
      map.querySourceFeatures('composite', {
        sourceLayer: 'building',
      }) as unknown as TileBuildingFeature[],
      pt,
    );
    scheduleIdleDispose();
    return bld ? { ring: bld, kind: 'building' } : null;
  } catch {
    // 実行中にプローブが壊れた (remove 済み等) → 破棄して次回作り直す。
    disposeMapboxProbe();
    return null;
  }
}

/**
 * 対象点の「敷地 (landuse) > 建物」の実形状を Mapbox タイルから取得。
 * どちらも無い (空地・道路上の点) は null → 呼び出し側は GSI/OSM/円へフォールバック。
 * トークン未設定 / 非ブラウザ環境では null (機能を無効化)。
 */
export function fetchMapboxSite(
  center: LngLat,
): Promise<MapboxSiteShape | null> {
  if (!MAPBOX_TOKEN || typeof document === 'undefined') {
    return Promise.resolve(null);
  }
  const run = chain.then(() => doFetch(center)).catch(() => null);
  // チェーンは失敗しても途切れさせない (次の呼び出しを止めない)。
  chain = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

/**
 * 敷地を採ってよいか。上限 (m²) を超える敷地は、公園の中の学校などが公園全体を
 * 拾った形なので採らない (呼び出し側は建物の形へ回す)。上限なしなら常に採る。
 */
export function acceptGrounds(ring: Array<[number, number]>, maxAreaM2?: number): boolean {
  return maxAreaM2 === undefined || ringAreaM2(ring) <= maxAreaM2;
}

/**
 * リングだけ欲しい呼び出し側 (v1/v2 の形状解決) 用の簡易形。
 * maxGroundsAreaM2 を渡すと、それを超える敷地は採らず、点を含む建物の形を返す
 * (lib/evacuation/site-size.ts の maxSiteAreaM2 で施設の名前から決める)。
 */
export async function fetchMapboxFootprint(
  center: LngLat,
  opts: { maxGroundsAreaM2?: number } = {},
): Promise<Array<[number, number]> | null> {
  const site = await fetchMapboxSite(center);
  if (!site) return null;
  if (site.kind === 'grounds' && !acceptGrounds(site.ring, opts.maxGroundsAreaM2)) {
    return fetchMapboxBuilding(center);
  }
  return site.ring;
}

/**
 * 対象点を内包する **建物** の輪郭だけを Mapbox タイルから取得する (敷地は見ない)。
 *
 * 物件の塗り用。物件座標が公園や学校の敷地内に落ちても敷地全体を掴まず、その建物の
 * 輪郭だけを返す (Overpass の 'building-only' と同じ方針)。建物が無ければ null。
 * fetchMapboxSite (敷地優先) と同じプローブ・直列化を共有する。
 */
export function fetchMapboxBuilding(
  center: LngLat,
): Promise<Array<[number, number]> | null> {
  if (!MAPBOX_TOKEN || typeof document === 'undefined') {
    return Promise.resolve(null);
  }
  const run = chain.then(() => doFetchBuilding(center)).catch(() => null);
  chain = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

/** 建物レイヤーだけを z16 で読み、点を内包する建物リングを返す (敷地はスキップ)。 */
async function doFetchBuilding(
  center: LngLat,
): Promise<Array<[number, number]> | null> {
  if (!probeReady) probeReady = createProbe();
  let map: mapboxgl.Map;
  try {
    map = await probeReady;
  } catch {
    disposeMapboxProbe();
    return null;
  }
  const pt: [number, number] = [center.lng, center.lat];
  try {
    map.jumpTo({ center: pt, zoom: BUILDING_ZOOM });
    await waitTilesReady(map);
    const bld = buildingRingFromFeatures(
      map.querySourceFeatures('composite', {
        sourceLayer: 'building',
      }) as unknown as TileBuildingFeature[],
      pt,
    );
    scheduleIdleDispose();
    return bld ?? null;
  } catch {
    disposeMapboxProbe();
    return null;
  }
}

/**
 * start→end の直線近傍で「歩いて渡れる橋」を Mapbox `road` タイルから列挙する。
 *
 * Overpass 版 (fetchWalkableBridgesNearSegment) の安定代替。取得はベクタタイルの
 * HTTP 直読み (services/mapbox-road-tiles.ts) — WebGL プローブ非依存。
 * **失敗は null**、「調べたが橋なし」は [] — 呼び出し側が null のときだけ
 * Overpass に落とせるように区別する。
 */
export async function fetchMapboxBridges(
  a: LngLat,
  b: LngLat,
  corridorMeters = 220,
  maxBridges = 4,
): Promise<BridgeCrossing[] | null> {
  const bbox = bboxOfPoints([a, b]);
  if (!bbox) return null;
  const feats = await fetchRoadFeaturesForBbox(bbox, corridorMeters);
  if (feats === null) return null;
  const bridges = feats.filter((f) => f.properties?.structure === 'bridge');
  return bridgesFromRoadFeatures(bridges, a, b, corridorMeters, maxBridges);
}

/** 進入点列挙: 境界リング bbox に足す余白 (m)。街路沿いサンプリング (25m) を覆う。 */
const ENTRY_PAD_M = 40;

/**
 * 避難先境界を横切る「歩行可能な道 (歩道・小道含む) の進入点」を列挙する。
 * OSM/Mapbox に入口 (entrance/gate) データが無い「地区」タイプの避難先で、
 * 実在の通路の入口を終点にするために使う。
 * 取得はベクタタイルの HTTP 直読み — 視野スイープが不要になり、リング全域を
 * 常に完全にカバーする (候補数が実行毎に揺れる問題の根治)。
 * **失敗は null**、「調べたが進入点なし」は []。
 */
export async function fetchMapboxBoundaryEntries(
  ring: Array<[number, number]>,
): Promise<LngLat[] | null> {
  if (ring.length < 3) return [];
  const feats = await fetchRoadFeaturesForBbox(ringBounds(ring), ENTRY_PAD_M);
  if (feats === null) return null;
  return boundaryEntriesFromRoadFeatures(feats, ring);
}

/** 近傍道路の探索半径ぶんの余白 (m)。nearbyRoadPointsFromFeatures の 45m を覆う。 */
const NEARBY_PAD_M = 60;

/**
 * 物件座標の近傍にある「歩ける道」を面ごとに列挙する (距離昇順)。
 * 経路始点の候補 (各道路面に面した物件の縁) に使う。呼び出し側は各面から経路を引き、
 * 「経路が最も短くなる面」を採用・他の面は経路候補として提示する。
 * 取得はベクタタイルの HTTP 直読み。失敗は null。
 *
 * ring (物件の輪郭) を渡すと、輪郭全体を覆う範囲を読み、端の面も候補にする
 * (細長い建物で端の道が落ちるため。nearbyRoadPointsFromFeatures 参照)。
 */
export async function fetchMapboxNearbyRoads(
  center: LngLat,
  ring?: Array<[number, number]>,
): Promise<LngLat[] | null> {
  const bbox =
    ring && ring.length >= 3
      ? ringBounds(ring)
      : ([center.lng, center.lat, center.lng, center.lat] as [
          number,
          number,
          number,
          number,
        ]);
  const feats = await fetchRoadFeaturesForBbox(bbox, NEARBY_PAD_M);
  if (feats === null) return null;
  return nearbyRoadPointsFromFeatures(feats, center, ring);
}

/** 中心線取得: 端点群の bbox に足す余白 (m)。迂回候補が bbox の外へ膨らむ分。 */
const CENTERLINE_PAD_M = 80;

/**
 * 経路の端点群 (始点面・終点・進入点) を覆う範囲の「車道中心線」を列挙する。
 * 経路スナップ (lib/geo/route-snap.ts) のスナップ先。取得はベクタタイルの HTTP
 * 直読み — WebGL プローブの故障でスナップが黙って無効化される事故が消える。
 * **失敗は null**、道なしは []。
 */
export async function fetchMapboxRoadCenterlines(
  points: LngLat[],
): Promise<Array<Array<[number, number]>> | null> {
  const bbox = bboxOfPoints(points);
  if (!bbox) return [];
  const feats = await fetchRoadFeaturesForBbox(bbox, CENTERLINE_PAD_M);
  if (feats === null) return null;
  return roadCenterlinesFromFeatures(feats);
}

/**
 * プローブ地図を破棄する。非アクティブ時に自動で呼ばれる (INACTIVITY_MS) ほか、
 * テスト後片付け・明示的なリソース解放にも使える。
 */
export function disposeMapboxProbe(): void {
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
  try {
    probe?.remove();
    probe?.getContainer()?.remove();
  } catch {
    /* noop */
  }
  probe = null;
  probeReady = null;
  chain = Promise.resolve();
}
